import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["newFormModal", "detailModal", "detailContent", "modalFooter", "completionForm", "newFollowupSection", "editForm", "scheduleFollowupForm", "quickFollowupForm", "meetingNotesForm", "modalType", "modalDate", "modalStatus"]

  connect() {
    // Bind escape key to close modals
    // Determine if this controller should operate in single-item mode (no navigation)
    const singleModeAttr = this.element.dataset.singleMode
    this.singleMode = singleModeAttr === "true" || singleModeAttr === "1"
    this.handleEscape = this.handleEscape.bind(this)
    this.handleKeyNavigation = this.handleKeyNavigation.bind(this)
    
    // Initialize - interactions will be set per contact when modal opens
    this.interactionItems = []
    this.currentInteractionIndex = -1
    this.currentContactId = null
  }

  openNewForm() {
    console.log("interaction-modal: openNewForm")
    if (this.hasNewFormModalTarget) {
      this.newFormModalTarget.style.display = "flex"
      this.newFormModalTarget.classList.add("modal--visible")
      document.addEventListener("keydown", this.handleEscape)
      document.body.style.overflow = "hidden"
    }
  }

  closeNewForm() {
    this.closeModal()
  }

  closeModal() {
    // Close all modals
    if (this.hasNewFormModalTarget) {
      this.newFormModalTarget.classList.remove("modal--visible")
      this.newFormModalTarget.style.display = "none"
    }
    if (this.hasDetailModalTarget) {
      this.detailModalTarget.classList.remove("modal--visible")
      this.detailModalTarget.style.display = "none"
    }
    
    document.removeEventListener("keydown", this.handleEscape)
    document.body.style.overflow = ""
  }

  closeAll() {
    // Close all modals and reset all forms
    this.closeModal()
    
    // Hide all sub-forms that might be open
    const allForms = document.querySelectorAll([
      '.interaction-detail__completion-form',
      '.interaction-detail__edit-form',
      '.interaction-detail__schedule-followup-form',
      '.interaction-detail__meeting-notes-form'
    ].join(','))
    
    allForms.forEach(form => {
      form.style.display = "none"
    })
    
    // Show all action divs that might have been hidden
    const actionDivs = document.querySelectorAll('.interaction-detail__actions')
    actionDivs.forEach(div => {
      div.style.display = "flex"
    })
  }

  openDetail(event) {
    event.preventDefault()
    const interactionId = event.currentTarget.dataset.interactionId
    
    // Get contact ID from multiple possible sources
    let contactId = event.currentTarget.dataset.contactId
    
    // If not directly on element, check parent controller
    if (!contactId) {
      const controllerElement = event.currentTarget.closest('[data-controller*="interaction-modal"][data-contact-id]')
      contactId = controllerElement ? controllerElement.dataset.contactId : null
    }
    
    // If still not found, check closest contact card
    if (!contactId) {
      const contactCard = event.currentTarget.closest('.contact-card[data-contact-id]')
      contactId = contactCard ? contactCard.dataset.contactId : null
    }
    
    // Update interaction items (skip building list in single mode)
    if (!this.singleMode && contactId) {
      this.currentContactId = contactId
      
      // Find all interaction items for this specific contact
      // First try within a contact-specific container
      let selector = `[data-contact-id="${contactId}"] [data-interaction-id]`
      
      // Also include items that have the contact ID directly
      let alternateSelector = `[data-interaction-id][data-contact-id="${contactId}"]`
      
      // Combine both selectors; exclude reminders (trigger-*) so prev/next only cycle interactions
      this.interactionItems = Array.from(document.querySelectorAll(`${selector}, ${alternateSelector}`))
        .filter(el => el.hasAttribute('data-action') && el.dataset.action.includes('openDetail'))
        .filter(el => !(el.dataset.interactionId || '').startsWith('trigger-'))

      // Remove duplicates based on interaction ID
      const seen = new Set()
      this.interactionItems = this.interactionItems.filter(item => {
        const id = item.dataset.interactionId
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    }
    
    // Find and set current index for navigation BEFORE rendering detail
    if (this.singleMode) {
      this.currentInteractionIndex = -1
    } else {
      this.currentInteractionIndex = this.interactionItems.findIndex(
        item => item.dataset.interactionId === interactionId
      )
    }

    // Now render the detail (navigation UI will use currentInteractionIndex)
    this.showInteractionDetail(interactionId)
  }

  showInteractionDetail(interactionId) {
    const detailElement = document.querySelector(`[data-interaction-detail="${interactionId}"]`)
    
    if (detailElement) {
      // Clone the content to avoid moving the original
      if (this.hasDetailContentTarget) {
        this.detailContentTarget.innerHTML = detailElement.innerHTML
        
        // Remove any existing navigation and re-add to reflect current state
        const existingNav = this.detailContentTarget.querySelector('.interaction-detail__navigation')
        if (existingNav) existingNav.remove()

        // Add navigation buttons to the content (only if a valid index is set)
        this.addNavigationButtons()
      }

      // Populate modal header from hidden detail template
      const typeEl = detailElement.querySelector('[data-interaction-type]')
      const dateEl = detailElement.querySelector('[data-interaction-date]')
      const statusEl = detailElement.querySelector('[data-interaction-status]')
      
      // Handle trigger vs regular interaction titles
      if (this.hasModalTypeTarget) {
        if (interactionId.startsWith('trigger-')) {
          this.modalTypeTarget.textContent = 'Edit Reminder'
        } else if (typeEl) {
          this.modalTypeTarget.textContent = typeEl.textContent.trim()
        }
      }
      
      if (this.hasModalDateTarget && dateEl) {
        this.modalDateTarget.textContent = dateEl.textContent.trim()
      }
      if (this.hasModalStatusTarget && statusEl) {
        const status = statusEl.textContent.trim()
        if (status === 'overdue') {
          this.modalStatusTarget.innerHTML = '<span class="contact-detail__modal-status-badge contact-detail__modal-status-badge--overdue">⚠️ OVERDUE</span>'
        } else if (status === 'upcoming') {
          this.modalStatusTarget.innerHTML = '<span class="contact-detail__modal-status-badge contact-detail__modal-status-badge--upcoming">📅 SCHEDULED</span>'
        } else {
          this.modalStatusTarget.innerHTML = ''
        }
      }
      if (this.hasDetailModalTarget) {
        this.detailModalTarget.style.display = "flex"
        this.detailModalTarget.classList.add("modal--visible")
      }
      document.addEventListener("keydown", this.handleEscape)
      document.addEventListener("keydown", this.handleKeyNavigation)
      document.body.style.overflow = "hidden"
    }
  }

  addNavigationButtons() {
    if (this.singleMode) return
    // Check if we have multiple interactions to navigate
    if (this.interactionItems.length <= 1) return
    // If current index is invalid (e.g., viewing a trigger), skip nav
    if (this.currentInteractionIndex == null || this.currentInteractionIndex < 0) return
    // Reminders (triggers) aren't part of the interaction sequence — no pagination
    const currentId = this.interactionItems[this.currentInteractionIndex]?.dataset.interactionId
    if (currentId && currentId.startsWith('trigger-')) return
    
    // Check if navigation already exists
    if (this.detailContentTarget.querySelector('.interaction-detail__navigation')) return
    
    // Create navigation container
    const navContainer = document.createElement('div')
    navContainer.className = 'interaction-detail__navigation'
    
    // Get current interaction status
    const detailElement = document.querySelector(`[data-interaction-detail="${this.interactionItems[this.currentInteractionIndex]?.dataset.interactionId}"]`)
    const statusEl = detailElement?.querySelector('[data-interaction-status]')
    const status = statusEl?.textContent.trim()
    let statusIndicator = ''
    if (status === 'overdue') {
      statusIndicator = ' <span class="interaction-detail__nav-status interaction-detail__nav-status--overdue">⚠️</span>'
    } else if (status === 'upcoming') {
      statusIndicator = ' <span class="interaction-detail__nav-status interaction-detail__nav-status--upcoming">📅</span>'
    }
    
    navContainer.innerHTML = `
      <button class="interaction-detail__nav-button interaction-detail__nav-button--prev" 
              data-action="click->interaction-modal#navigatePrev"
              ${this.currentInteractionIndex <= 0 ? 'disabled' : ''}>
        ← Previous
      </button>
      <span class="interaction-detail__nav-counter">
        ${this.currentInteractionIndex + 1} of ${this.interactionItems.length}${statusIndicator}
      </span>
      <button class="interaction-detail__nav-button interaction-detail__nav-button--next" 
              data-action="click->interaction-modal#navigateNext"
              ${this.currentInteractionIndex >= this.interactionItems.length - 1 ? 'disabled' : ''}>
        Next →
      </button>
    `
    
    // Insert at the beginning of the detail content
    this.detailContentTarget.insertBefore(navContainer, this.detailContentTarget.firstChild)
  }

  navigatePrev() {
    if (this.currentInteractionIndex > 0) {
      this.currentInteractionIndex--
      const prevItem = this.interactionItems[this.currentInteractionIndex]
      if (prevItem) {
        this.showInteractionDetail(prevItem.dataset.interactionId)
      }
    }
  }

  navigateNext() {
    if (this.currentInteractionIndex < this.interactionItems.length - 1) {
      this.currentInteractionIndex++
      const nextItem = this.interactionItems[this.currentInteractionIndex]
      if (nextItem) {
        this.showInteractionDetail(nextItem.dataset.interactionId)
      }
    }
  }

  closeDetail() {
    this.closeModal()
    document.removeEventListener("keydown", this.handleKeyNavigation)
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  handleKeyNavigation(event) {
    if (this.singleMode) return
    // Only handle arrow keys when modal is open and no form inputs are focused
    const focusedElement = document.activeElement
    const isFormInput = focusedElement.tagName === 'INPUT' || 
                       focusedElement.tagName === 'TEXTAREA' || 
                       focusedElement.tagName === 'SELECT'
    
    if (!isFormInput && this.hasDetailModalTarget && this.detailModalTarget.style.display === "flex") {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        this.navigatePrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        this.navigateNext()
      }
    }
  }

  showCompletionForm(event) {
    const detail = event.currentTarget.closest('.interaction-detail')
    if (!detail) return
    const completionForm = detail.querySelector('.interaction-detail__completion-form')
    const actionsDiv = detail.querySelector('.interaction-detail__actions')
    const editForm = detail.querySelector('.interaction-detail__edit-form')
    const scheduleForm = detail.querySelector('.interaction-detail__schedule-followup-form')

    if (editForm) editForm.style.display = "none"
    if (scheduleForm) scheduleForm.style.display = "none"

    if (completionForm && actionsDiv) {
      completionForm.style.display = "block"
      actionsDiv.style.display = "none"
    }
  }

  hideCompletionForm(event) {
    const completionForm = event.currentTarget.closest('.interaction-detail__completion-form')
    const interactionId = completionForm.dataset.interactionId
    const actionsDiv = document.querySelector(`[data-interaction-id="${interactionId}"]`).closest('.interaction-detail').querySelector('.interaction-detail__actions')
    
    if (completionForm) {
      completionForm.style.display = "none"
    }
    
    if (actionsDiv) {
      actionsDiv.style.display = "flex"
    }
  }

  toggleNewFollowup(event) {
    const isChecked = event.currentTarget.checked
    
    if (this.hasNewFollowupSectionTarget) {
      if (isChecked) {
        this.newFollowupSectionTarget.style.display = "block"
      } else {
        this.newFollowupSectionTarget.style.display = "none"
      }
    }
  }

  showEditForm(event) {
    const detail = event.currentTarget.closest('.interaction-detail')
    if (!detail) return
    const editForm = detail.querySelector('.interaction-detail__edit-form')
    const actionsDiv = detail.querySelector('.interaction-detail__actions')
    const completionForm = detail.querySelector('.interaction-detail__completion-form')
    const scheduleForm = detail.querySelector('.interaction-detail__schedule-followup-form')

    if (completionForm) completionForm.style.display = "none"
    if (scheduleForm) scheduleForm.style.display = "none"

    if (editForm && actionsDiv) {
      editForm.style.display = "block"
      actionsDiv.style.display = "none"
    }
  }

  hideEditForm(event) {
    const editForm = event.currentTarget.closest('.interaction-detail__edit-form')
    const interactionId = editForm.dataset.interactionId
    const interactionDetail = editForm.closest('.interaction-detail')
    const actionsDiv = interactionDetail.querySelector('.interaction-detail__actions')
    
    if (editForm) {
      editForm.style.display = "none"
    }
    
    if (actionsDiv) {
      actionsDiv.style.display = "flex"
    }
  }

  markAsCompleted(event) {
    const interactionId = event.currentTarget.dataset.interactionId
    const contactId = this.element.dataset.contactId || this.currentContactId
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/home/contacts/${contactId}/interactions/${interactionId}/mark_completed`
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)
    
    // Add method override for PATCH
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'PATCH'
    form.appendChild(methodInput)
    
    document.body.appendChild(form)
    form.submit()
  }

  showScheduleFollowup(event) {
    const detail = event.currentTarget.closest('.interaction-detail')
    if (!detail) return
    const scheduleForm = detail.querySelector('.interaction-detail__schedule-followup-form')
    const actionsDiv = detail.querySelector('.interaction-detail__actions')
    const editForm = detail.querySelector('.interaction-detail__edit-form')
    const completionForm = detail.querySelector('.interaction-detail__completion-form')

    if (editForm) editForm.style.display = "none"
    if (completionForm) completionForm.style.display = "none"

    if (scheduleForm && actionsDiv) {
      scheduleForm.style.display = "block"
      actionsDiv.style.display = "none"
    }
  }

  hideScheduleFollowup(event) {
    const scheduleForm = event.currentTarget.closest('.interaction-detail__schedule-followup-form')
    const interactionId = scheduleForm.dataset.interactionId
    const interactionDetail = scheduleForm.closest('.interaction-detail')
    const actionsDiv = interactionDetail.querySelector('.interaction-detail__actions')
    
    if (scheduleForm) {
      scheduleForm.style.display = "none"
    }
    
    if (actionsDiv) {
      actionsDiv.style.display = "flex"
    }
  }

  showQuickFollowup(event) {
    const interactionId = event.currentTarget.dataset.interactionId
    const quickForm = document.querySelector(`[data-interaction-id="${interactionId}"].quick-followup-form`)
    
    if (quickForm) {
      quickForm.style.display = "block"
    }
  }

  hideQuickFollowup(event) {
    const quickForm = event.currentTarget.closest('.quick-followup-form')
    
    if (quickForm) {
      quickForm.style.display = "none"
    }
  }

  toggleFollowupForm(event) {
    const interactionId = event.currentTarget.dataset.interactionId
    const followupSection = document.querySelector(`.interaction-detail__create-followup[data-interaction-id="${interactionId}"]`)
    
    if (followupSection) {
      if (followupSection.style.display === "none" || !followupSection.style.display) {
        followupSection.style.display = "block"
        event.currentTarget.textContent = "Cancel Follow-up"
      } else {
        followupSection.style.display = "none"
        event.currentTarget.textContent = "Create Follow-up"
      }
    }
  }

  showMeetingNotesForm(event) {
    const detail = event.currentTarget.closest('.interaction-detail')
    if (!detail) return
    const meetingNotesForm = detail.querySelector('.interaction-detail__meeting-notes-form')
    const actionsDiv = detail.querySelector('.interaction-detail__actions')
    const editForm = detail.querySelector('.interaction-detail__edit-form')
    const scheduleForm = detail.querySelector('.interaction-detail__schedule-followup-form')
    const completionForm = detail.querySelector('.interaction-detail__completion-form')

    if (editForm) editForm.style.display = "none"
    if (scheduleForm) scheduleForm.style.display = "none"
    if (completionForm) completionForm.style.display = "none"

    if (meetingNotesForm && actionsDiv) {
      meetingNotesForm.style.display = "block"
      actionsDiv.style.display = "none"

      const textarea = meetingNotesForm.querySelector('textarea')
      if (textarea) textarea.focus()
    }
  }

  hideMeetingNotesForm(event) {
    const meetingNotesForm = event.currentTarget.closest('.interaction-detail__meeting-notes-form')
    const interactionId = meetingNotesForm.dataset.interactionId
    const interactionDetail = meetingNotesForm.closest('.interaction-detail')
    const actionsDiv = interactionDetail.querySelector('.interaction-detail__actions')
    
    if (meetingNotesForm) {
      meetingNotesForm.style.display = "none"
      
      // Clear the form
      const textarea = meetingNotesForm.querySelector('textarea')
      if (textarea) {
        textarea.value = ""
      }
    }
    
    if (actionsDiv) {
      actionsDiv.style.display = "flex"
    }
  }

  async submitMemo(event) {
    event.preventDefault()
    const form = event.currentTarget
    const detail = form.closest('.interaction-detail')
    if (!detail) return

    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]')
    if (submitBtn) submitBtn.disabled = true

    try {
      const resp = await fetch(form.action, {
        method: form.method || 'POST',
        body: new FormData(form),
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
        credentials: 'same-origin',
      })
      if (!resp.ok) throw new Error(`Save failed (${resp.status})`)
      const data = await resp.json()

      const formContainer = form.closest('.interaction-detail__meeting-notes-form')
      const interactionId = formContainer?.dataset.interactionId

      // Update the visible modal copy
      this.applyMemoUpdate(detail, data)
      // Update the hidden source template so reopen stays consistent
      if (interactionId) {
        const sourceDetail = document.querySelector(`[data-interaction-detail="${interactionId}"] .interaction-detail`)
        if (sourceDetail && sourceDetail !== detail) this.applyMemoUpdate(sourceDetail, data)
      }

      const actions = detail.querySelector('.interaction-detail__actions')
      if (formContainer) formContainer.style.display = 'none'
      if (actions) actions.style.display = 'flex'
    } catch (err) {
      console.error(err)
      alert('Failed to save memo. Please try again.')
    } finally {
      if (submitBtn) submitBtn.disabled = false
    }
  }

  applyMemoUpdate(detail, data) {
    // Upsert the Memo display section (insert after Purpose if present, else at top)
    let memoSection = Array.from(detail.querySelectorAll(':scope > .interaction-detail__section'))
      .find(s => s.querySelector('.interaction-detail__label')?.textContent.trim() === 'Memo')
    if (data.has_meeting_notes) {
      if (!memoSection) {
        memoSection = document.createElement('div')
        memoSection.className = 'interaction-detail__section'
        const purpose = Array.from(detail.querySelectorAll(':scope > .interaction-detail__section'))
          .find(s => s.querySelector('.interaction-detail__label')?.textContent.trim() === 'Purpose')
        if (purpose && purpose.nextSibling) {
          detail.insertBefore(memoSection, purpose.nextSibling)
        } else if (purpose) {
          detail.appendChild(memoSection)
        } else {
          detail.insertBefore(memoSection, detail.firstChild)
        }
      }
      memoSection.innerHTML = `<h4 class="interaction-detail__label">Memo</h4><div class="interaction-detail__content">${data.meeting_notes_html}</div>`
    } else if (memoSection) {
      memoSection.remove()
    }

    // Update Add/Edit button label + style
    const memoBtn = detail.querySelector('[data-action*="showMeetingNotesForm"]')
    if (memoBtn) {
      memoBtn.textContent = data.has_meeting_notes ? 'Edit memo' : 'Add memo'
      memoBtn.classList.remove('button--primary', 'button--outline')
      memoBtn.classList.add(data.has_meeting_notes ? 'button--outline' : 'button--primary')
    }

    // Mark-done button promotes to primary once memo exists
    const markDoneBtn = detail.querySelector('[data-action*="markAsCompleted"]')
    if (markDoneBtn) {
      markDoneBtn.classList.remove('button--primary', 'button--outline')
      markDoneBtn.classList.add(data.has_meeting_notes ? 'button--primary' : 'button--outline')
    }

    // Keep the textarea in the form synced with the latest server value
    const textarea = detail.querySelector('.interaction-detail__meeting-notes-form textarea')
    if (textarea) textarea.value = data.meeting_notes || ''
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    document.removeEventListener("keydown", this.handleKeyNavigation)
    document.body.style.overflow = ""
  }
}